import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { api, apiError, timeOf } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Photo } from "@/components/Photo";
import { toast } from "sonner";
import { Printer, PaperPlaneTilt, ArrowLeft } from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const kindLabel = { in: "Checked in", out: "Checked out", absent: "Marked absent" };

export default function DaySheet() {
  const { studentId } = useParams();
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const day = params.get("day") || new Date().toISOString().slice(0, 10);
  const [sheet, setSheet] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/daily-sheet/${studentId}`, { params: { day } });
      setSheet(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  }, [studentId, day]);

  useEffect(() => { load(); }, [load]);

  const emailIt = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/daily-sheet/${studentId}/email?day=${day}`);
      toast.success(`Day sheet emailed to ${data.sent_to}`);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
    setBusy(false);
  };

  if (!sheet) return <p className="text-muted-foreground" data-testid="daysheet-loading">Loading…</p>;

  const back = user?.role === "parent" ? "/portal" : "/admin/attendance";

  return (
    <div data-testid="daysheet-page">
      <div className="flex flex-wrap justify-between items-end gap-4 print:hidden">
        <Link to={back} className="inline-flex items-center gap-2 text-sm font-bold text-muted-foreground hover:text-[#3B82F6]"
              style={{ transition: "color .2s ease" }} data-testid="daysheet-back">
          <ArrowLeft size={16} /> Back
        </Link>
        <div className="flex flex-wrap gap-3 items-end">
          <Input type="date" value={day} onChange={(e) => setParams({ day: e.target.value })}
                 className="h-11 rounded-xl w-44" data-testid="daysheet-date" />
          <Button variant="outline" onClick={() => window.print()} className="h-11 px-6 rounded-full font-bold"
                  data-testid="daysheet-print-btn"><Printer size={17} className="mr-2" /> Print</Button>
          {user?.role !== "parent" && (
            <Button onClick={emailIt} disabled={busy}
                    className="h-11 px-6 rounded-full bg-[#3B82F6] hover:bg-[#2563EB] font-bold"
                    data-testid="daysheet-email-btn">
              <PaperPlaneTilt size={17} className="mr-2" /> {busy ? "Sending…" : "Email to family"}
            </Button>
          )}
        </div>
      </div>

      <div className="mt-6 bg-white rounded-3xl border border-border p-8 max-w-3xl print:border-0 print:p-0"
           data-testid="daysheet-body">
        <div className="flex flex-wrap justify-between gap-4 pb-6 border-b border-border">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-[#3B82F6]">Daily Report</p>
            <p className="mt-2 font-display text-3xl font-bold" data-testid="daysheet-child">
              {sheet.student.name}
            </p>
            <p className="text-sm text-muted-foreground mt-1 font-semibold">
              {sheet.student.room} · {sheet.student.program}
            </p>
          </div>
          <div className="text-right">
            <p className="font-display text-xl font-semibold">
              {new Date(`${sheet.day}T12:00:00`).toLocaleDateString("en-US",
                { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
            </p>
            <p className="text-sm text-muted-foreground mt-1">{sheet.program.name}</p>
            <p className="text-sm text-muted-foreground">{sheet.program.phone}</p>
          </div>
        </div>

        {sheet.student.allergies && (
          <p className="mt-5 text-sm font-bold text-[#EF4444]" data-testid="daysheet-allergies">
            Allergies: {sheet.student.allergies}
          </p>
        )}

        <h2 className="mt-6 font-display text-lg font-semibold">Arrival & departure</h2>
        <div className="mt-3 space-y-2" data-testid="daysheet-attendance">
          {sheet.attendance.length === 0 && (
            <p className="text-sm text-muted-foreground">No check-in recorded for this day.</p>
          )}
          {sheet.attendance.map((e) => (
            <div key={`${e.kind}-${e.at}`} className="flex justify-between text-sm border-b border-border pb-2">
              <span className="font-semibold">{kindLabel[e.kind] || e.kind}</span>
              <span className="text-muted-foreground">
                {timeOf(e.at)}{e.guardian ? ` · ${e.guardian}` : ""}
              </span>
            </div>
          ))}
        </div>

        {sheet.groups.length === 0 ? (
          <p className="mt-6 text-sm text-muted-foreground" data-testid="daysheet-empty">
            No activities were logged for this day yet.
          </p>
        ) : (
          sheet.groups.map((g) => (
            <div key={g.type} className="mt-6" data-testid={`daysheet-group-${g.type}`}>
              <h2 className="font-display text-lg font-semibold capitalize">{g.type}</h2>
              <div className="mt-3 space-y-3">
                {g.items.map((it) => (
                  <div key={it.id} className="border-b border-border pb-3">
                    <div className="flex justify-between gap-4 text-sm">
                      <span className="font-semibold">{it.detail || g.type}</span>
                      <span className="text-muted-foreground whitespace-nowrap">{timeOf(it.at)}</span>
                    </div>
                    {it.photo_path && (
                      <Photo path={it.photo_path} alt={it.detail || "Photo"}
                             className="mt-2 rounded-xl w-full max-w-sm h-48 object-cover"
                             testid={`daysheet-photo-${it.id}`} />
                    )}
                    <p className="text-xs text-muted-foreground mt-1">{it.staff}</p>
                  </div>
                ))}
              </div>
            </div>
          ))
        )}

        <p className="mt-8 text-xs text-muted-foreground">
          {sheet.program.name} · {sheet.program.address} · {sheet.program.phone}
        </p>
      </div>
    </div>
  );
}
